// Shared client-side photo handling for uploads (public form + case page).
// Field techs are on phones, where a raw camera photo (4-12MB, sometimes HEIC)
// often fails to upload over a weak signal. We downscale + re-encode to JPEG in
// the browser first, which makes uploads fast and reliable and sidesteps HEIC
// format quirks. Non-image files (e.g. PDF) pass through untouched.

const MAX_UPLOAD_DIM = 1600 // px, longest edge
const JPEG_QUALITY = 0.82

// Accept attribute + validation for the attachment inputs.
export const ATTACHMENT_ACCEPT = 'image/*,application/pdf,.heic,.heif'

export function isHeic(file: File): boolean {
  return /image\/hei[cf]/i.test(file.type) || /\.(heic|heif)$/i.test(file.name)
}

export function isPdf(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
}

// Images (incl. HEIC) and PDFs are allowed; everything else is rejected.
export function isAllowedAttachment(file: File): boolean {
  return file.type.startsWith('image/') || isHeic(file) || isPdf(file)
}

// Decode a photo to an ImageBitmap. Native path handles JPEG/PNG/WebP/GIF/BMP,
// and HEIC on Apple/WebKit browsers. When native decode fails on a HEIC/HEIF
// (e.g. Samsung HEIF opened in Android Chrome), a decoder is lazily loaded to
// convert it to JPEG first — so it's downloaded only in that rare case.
async function decodeToBitmap(file: File): Promise<ImageBitmap> {
  try {
    // `from-image` applies EXIF orientation so phone photos aren't sideways.
    return await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions)
  } catch {
    try {
      return await createImageBitmap(file)
    } catch (err) {
      if (isHeic(file)) {
        // heic2any is a UMD module (sets both module.exports and .default to the
        // function) — resolve the callable across possible interop shapes.
        const mod: unknown = await import('heic2any')
        const m = mod as { default?: unknown } & Record<string, unknown>
        const convert = (
          typeof mod === 'function'
            ? mod
            : typeof m.default === 'function'
              ? m.default
              : typeof (m.default as { default?: unknown })?.default === 'function'
                ? (m.default as { default: unknown }).default
                : null
        ) as
          | ((o: { blob: Blob; toType?: string; quality?: number }) => Promise<Blob | Blob[]>)
          | null
        if (!convert) throw new Error('heic decoder unavailable')
        const converted = await convert({ blob: file, toType: 'image/jpeg', quality: 0.9 })
        const jpeg = Array.isArray(converted) ? converted[0] : converted
        return await createImageBitmap(jpeg)
      }
      throw err
    }
  }
}

// Downscale + JPEG-encode an image for reliable upload. PDFs and anything that
// isn't an image are returned unchanged. Falls back to the original file if
// anything goes wrong — never blocks a submission.
export async function compressPhoto(file: File): Promise<File> {
  try {
    if (!file.type.startsWith('image/') && !isHeic(file)) return file
    const bitmap = await decodeToBitmap(file)
    const scale = Math.min(1, MAX_UPLOAD_DIM / Math.max(bitmap.width, bitmap.height))
    const w = Math.max(1, Math.round(bitmap.width * scale))
    const h = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, w, h)
    bitmap.close?.()
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY))
    if (!blob) return file
    // Keep the original only if it's already a widely-viewable format AND smaller.
    // HEIC/HEIF must always become JPEG (desktop browsers can't display HEIC),
    // even if the re-encode ends up marginally larger.
    if (blob.size >= file.size && !isHeic(file)) return file
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg'
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() })
  } catch {
    return file
  }
}
